import React from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Box, Container, Typography, Link, Paper, Button } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Markdown & Math imports
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw"; 
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import "katex/dist/katex.min.css";

import { notes } from "./writing";

function NotePage() {
  const { id } = useParams();
  const theme = useTheme();
  const note = notes.find(n => n.id === id);

  // 1. Handle Not Found
  if (!note) {
    return (
      <Container sx={{ pt: "150px" }}>
        <Typography variant="h4">Note Not Found</Typography>
        <Link component={RouterLink} to="/notes" sx={{ mt: 2, display: 'block' }}>
          ← Back to Index
        </Link>
      </Container>
    );
  }

  // 2. Main Render
  return (
    <Box
      sx={{
        minHeight: "100vh",
        pt: "100px",
        pb: "100px",
        background: theme.palette.background.default,
        color: theme.palette.text.primary
      }}
    >
      <Container maxWidth="md">
        {/* Back Button */}
        <Button
          component={RouterLink}
          to="/notes"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3, color: 'text.secondary', textTransform: 'none' }}
        >
          Back to Notes
        </Button>

        {/* The Note Card/Paper */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            backgroundColor: theme.palette.background.card || theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          {/* Note Title */}
          <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
            {note.title}
          </Typography>
          
          {/* Note Category Metadata */}
          <Typography variant="subtitle2" sx={{ opacity: 0.6, mb: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
             {note.category}
          </Typography>

          {/* Content Renderer */}
          <Box sx={{
            // Global Markdown Styles
            '& h2': { fontSize: '1.5rem', mt: 4, mb: 2, borderBottom: `1px solid ${theme.palette.divider}`, pb: 1 },
            '& h3': { fontSize: '1.25rem', mt: 3, mb: 1, fontWeight: 600 },
            '& p': { fontSize: '1.05rem', lineHeight: 1.7, mb: 2 },
            '& li': { fontSize: '1.05rem', lineHeight: 1.7, mb: 1 },
            '& blockquote': { 
                borderLeft: `4px solid ${theme.palette.primary.main}`, 
                pl: 2, fontStyle: 'italic', my: 2, opacity: 0.8 
            },

            // 👇 MINIMAL DROPDOWN STYLING 👇
'& details': {
                marginBottom: '1rem',
                marginLeft: '3.5rem', // <--- INCREASED from 1.5rem to 3.5rem
            },
            '& summary': {
                cursor: 'pointer',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: theme.palette.text.disabled, 
                listStyle: 'none',
                transition: 'color 0.2s',
                userSelect: 'none',
                display: 'flex',       // Ensures strict alignment
                alignItems: 'center'
            },
            '& summary:hover': {
                color: theme.palette.primary.main,
            },
            // The Tiny Arrow
            '& summary::before': {
                content: '"▶"',
                display: 'inline-block',
                fontSize: '0.6rem',
                marginRight: '8px',
                transition: 'transform 0.2s'
            },
            '& details[open] summary::before': {
                transform: 'rotate(90deg)', 
            },
            '& details[open]': {
                // borderLeft: `1px solid ${theme.palette.divider}`, // Optional: Add line if you want
                // paddingLeft: '12px',
                marginTop: '8px'
            }
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex, rehypeRaw]}
              components={{
                code({ inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || "");
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={theme.palette.mode === "dark" ? oneDark : oneLight}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                      customStyle={{ 
                        borderRadius: '8px', 
                        margin: '1.5em 0',
                        fontSize: '0.9rem' 
                      }}
                    >
                      {String(children).replace(/\n$/, "")}
                    </SyntaxHighlighter>
                  ) : (
                    <code
                      style={{
                        backgroundColor: theme.palette.action.hover,
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontFamily: 'monospace',
                        fontSize: '0.9em'
                      }}
                      {...props}
                    >
                      {children}
                    </code>
                  );
                }
              }}
            >
              {note.content}
            </ReactMarkdown>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default NotePage;